<template>
  <header class="sticky top-0 z-50 border-b border-ink-100 bg-white/80 backdrop-blur-xl"
    :class="scrolled ? 'shadow-[0_8px_30px_-18px_rgba(0,0,0,.28)]' : ''" aria-label="Site header">
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 md:h-20 flex items-center justify-between">
      <!-- Logo -->
      <router-link :to="`/${langue}`"
        class="flex items-center gap-3 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-300 rounded-lg">
        <img src="@/assets/img/logo/ar.svg" alt="Anjah R." class="h-16 md:h-20 w-auto" />
        <span class="sr-only">Accueil</span>
      </router-link>
      <a href="/login?auto=true"
        class="inline-flex items-center gap-2 px-4 py-2 text-sm font-bold text-white shadow-lg rounded-full bg-gradient-to-r from-slate-800 to-slate-900 hover:shadow-xl transition-all transform hover:-translate-y-0.5">
        <i class="fa-solid fa-gauge-high"></i>
        <span>Retour Admin</span>
      </a>


      <!-- Menu desktop -->
      <nav class="hidden md:flex items-center gap-1 relative" aria-label="Primary">
        <template v-for="(nav, i) in navLinks" :key="i">
          <!-- Liens externes -->
          <a v-if="isExternal(nav.action)" :href="nav.action" target="_blank" rel="noopener"
            class="relative px-3 py-2 rounded-lg text-ink-700 hover:text-brand-600 transition-colors duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-300">
            <span class="font-medium">{{ nav.label || 'Lien' }}</span>
            <i class="fa-solid fa-arrow-up-right-from-square ml-1 text-[10px] opacity-80"></i>
            <span
              class="pointer-events-none absolute left-3 right-3 -bottom-[2px] h-[2px] rounded-full bg-gradient-to-r from-brand-300 to-brand-600 opacity-0 transition-opacity duration-300"
              :class="hoverUnderlineClass(false)" />
          </a>

          <!-- Liens internes -->
          <router-link v-else :to="localized(nav.action)"
            class="relative px-6 py-2 rounded-lg text-ink-700 hover:text-brand-600 transition-colors duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-300"
            :class="isActive(localized(nav.action)) ? 'text-brand-600' : ''">
            <span class="font-medium">{{ nav.label || 'Lien' }}</span>
            <span
              class="pointer-events-none absolute left-3 right-3 -bottom-[2px] h-[2px] rounded-full bg-gradient-to-r from-brand-300 to-brand-600 transition-all duration-300 ease-in-out"
              :class="isActive(localized(nav.action)) ? 'opacity-100' : 'opacity-0'" />
          </router-link>
        </template>
      </nav>

      <!-- Langue (desktop) -->
      <div class="relative hidden md:block">
        <button @click="toggleLangDropdown"
          class="flex items-center gap-2 px-3 py-2 rounded-full border border-ink-200 bg-white text-ink-800 hover:bg-ink-50 transition"
          aria-haspopup="listbox" :aria-expanded="isLangDropdownOpen ? 'true' : 'false'"
          :aria-label="langue === 'fr' ? 'Langue : Français' : 'Language: English'">
          <span :class="`fi ${langue === 'fr' ? 'fi-ca' : 'fi-us'} w-5 h-3 rounded-sm shadow-sm`"></span>
          <span class="ml-1 text-sm font-medium">{{ langue === 'fr' ? 'FR' : 'EN' }}</span>
          <i class="fas fa-chevron-down text-xs"></i>
        </button>

        <ul v-if="isLangDropdownOpen"
          class="absolute right-0 mt-2 w-40 bg-white border border-ink-200 rounded-xl shadow-[0_16px_40px_-20px_rgba(0,0,0,.35)] overflow-hidden"
          role="listbox">
          <li>
            <button class="w-full text-left px-3 py-2 hover:bg-ink-50 flex items-center gap-2"
              @click="changeLanguage('fr')">
              <span class="fi fi-ca w-5 h-3 rounded-sm shadow-sm"></span>
              <span>Français</span>
            </button>
          </li>
          <li>
            <button class="w-full text-left px-3 py-2 hover:bg-ink-50 flex items-center gap-2"
              @click="changeLanguage('en')">
              <span class="fi fi-us w-5 h-3 rounded-sm shadow-sm"></span>
              <span>English</span>
            </button>
          </li>
        </ul>
      </div>

      <!-- Burger -->
      <button
        class="md:hidden rounded-lg p-2 hover:bg-ink-100 text-ink-800 transition focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-300"
        @click="isMobileMenuOpen = !isMobileMenuOpen" :aria-expanded="isMobileMenuOpen ? 'true' : 'false'"
        aria-controls="mobile-menu" aria-label="Menu">
        <i :class="isMobileMenuOpen ? 'fas fa-xmark' : 'fas fa-bars'"></i>
      </button>
    </div>

    <!-- Mobile menu -->
    <transition name="slide">
      <div v-if="isMobileMenuOpen" id="mobile-menu" class="md:hidden border-t border-ink-100 bg-white"
        @keydown.escape="isMobileMenuOpen = false">
        <nav class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 flex flex-col gap-1" aria-label="Mobile">
          <template v-for="(nav, i) in navLinks" :key="'m' + i">
            <!-- externes -->
            <a v-if="isExternal(nav.action)" :href="nav.action" target="_blank" rel="noopener"
              @click="isMobileMenuOpen = false"
              class="relative block px-3 py-2 rounded-lg text-ink-800 hover:bg-blue-50 hover:text-brand-600 transition-colors duration-200">
              <span class="font-medium">{{ nav.label || 'Lien' }}</span>
              <i class="fa-solid fa-arrow-up-right-from-square ml-1 text-[10px] opacity-80"></i>
            </a>

            <!-- internes -->
            <router-link v-else :to="localized(nav.action)" @click="isMobileMenuOpen = false"
              class="relative block px-3 py-2 rounded-lg text-ink-800 hover:bg-blue-50 hover:text-brand-600 transition-colors duration-200"
              :class="isActive(localized(nav.action)) ? 'text-brand-600' : ''">
              <span class="font-medium">{{ nav.label || 'Lien' }}</span>
              <span v-if="isActive(localized(nav.action))"
                class="absolute bottom-0 left-0 w-full h-[2px] rounded-full bg-gradient-to-r from-brand-300 to-brand-600" />
            </router-link>
          </template>

          <div class="pt-3 mt-2 border-t border-ink-100">
            <label class="block mb-2 text-sm font-semibold text-ink-700">
              {{ langue === 'fr' ? 'Choisir la langue' : 'Choose language' }}
            </label>
            <div class="grid grid-cols-2 gap-2">
              <button
                class="flex items-center justify-center gap-2 px-3 py-2 rounded-lg border border-ink-200 hover:bg-ink-50"
                @click="changeLanguage('fr')">
                <span class="fi fi-ca w-5 h-3 rounded-sm shadow-sm"></span>
                <span>Français</span>
              </button>
              <button
                class="flex items-center justify-center gap-2 px-3 py-2 rounded-lg border border-ink-200 hover:bg-ink-50"
                @click="changeLanguage('en')">
                <span class="fi fi-us w-5 h-3 rounded-sm shadow-sm"></span>
                <span>English</span>
              </button>
            </div>
          </div>
        </nav>
      </div>
    </transition>
  </header>
</template>

<!-- <script>
import { mapGetters } from "vuex";
import http, { apiUrl } from "@/api/http";
import { fetchPageIdByCode } from "@/utils/pageUtils";

export default {
  name: "HighHeader",
  data() {
    return {
      navLinks: [],
      isMobileMenuOpen: false,
      isLangDropdownOpen: false,
      scrolled: false,
      _unwatchRoute: null,
    };
  },
  computed: {
    ...mapGetters(["getLangue"]),
    langue: {
      get() {
        return this.getLangue;
      },
      set(v) {
        this.$store.commit("setLangue", v);
      },
    },
  },
  methods: {
    // async fetchPages(lang) {
    //   const l = String(lang || this.langue || "fr").trim();
    //   if (!l) return;
      
    //   try {
    //     const url = `${apiUrl("GlobalContent")}?type=navHeader&langue=${encodeURIComponent(l)}`;
    //     const { data } = await http.get(url);
    //     this.navLinks = Array.isArray(data?.boutons) ? data.boutons : [];
    //   } catch (e) {
    //     console.error("Erreur chargement nav:", e);
    //   }
    // },
    async fetchPages(lang) {
  // On récupère la langue, ou celle du store, ou 'fr' par défaut, en s'assurant que ce n'est pas "undefined"
  let l = String(lang || this.$route.params.langue || this.langue || "").trim();
  
  // 🛡️ GARDE DE SÉCURITÉ : Si la langue est invalide ou "undefined", on ne fait RIEN.
  if (!l || l === "undefined" || l === "null") {
    console.warn("[HighHeader] Langue non prête, appel annulé.");
    return;
  }

  try {
    const url = `${apiUrl("GlobalContent")}?type=navHeader&langue=${encodeURIComponent(l)}`;
    const { data } = await http.get(url);
    this.navLinks = Array.isArray(data?.boutons) ? data.boutons : [];
  } catch (e) {
    // On ignore les erreurs 400 silencieusement pour ne pas polluer la console au démarrage
    if (e.response && e.response.status === 400) return;
    console.error("Erreur chargement nav:", e);
  }
},

    // ---------------- Utils liens ----------------
    isExternal(path) {
      return /^https?:\/\//i.test(String(path || ""));
    },
    ensureLocalePrefix(path, lang) {
      if (!path) return `/${lang}`;
      if (this.isExternal(path)) return path;
      if (/^\/(fr|en)\b/.test(path)) return path.replace(/^\/(fr|en)/, `/${lang}`);
      return path.startsWith("/") ? `/${lang}${path}` : `/${lang}/${path}`;
    },
    localized(path) {
      return this.ensureLocalePrefix(path, this.langue);
    },
    setLanguage(lang) {
      this.$store.commit("setLangue", lang);
      localStorage.setItem("locale", lang);
    },

    // ---------------- Routes Services helpers ----------------
    parseServiceDetailPath(path) {
      const m = /^\/(fr|en)\/services\/([^\/?#]+)/i.exec(String(path || ""));
      if (!m) return null;
      return { lang: m[1], slug: decodeURIComponent(m[2]) };
    },
    parseServiceTagPath(path) {
      const m = /^\/(fr|en)\/services\/tag\/([^\/?#]+)/i.exec(String(path || ""));
      if (!m) return null;
      return { lang: m[1], tag: decodeURIComponent(m[2]) };
    },

    async resolveDetailSlugForLang(targetLang) {
      const info = this.parseServiceDetailPath(this.$route.path);
      if (!info) return null;
      try {
        const src = await fetchPageIdByCode("home", info.lang);
        const dst = await fetchPageIdByCode("home", targetLang);
        const source_page_id = Number(typeof src === "number" ? src : (src?.page_id ?? src?.id));
        const target_page_id = Number(typeof dst === "number" ? dst : (dst?.page_id ?? dst?.id));
        if (!Number.isFinite(source_page_id) || !Number.isFinite(target_page_id)) return null;

        const { data } = await http.get("service-detail/resolve-slug", {
          params: { source_page_id, target_page_id, slug: info.slug },
        });
        return data?.found && data?.slug ? String(data.slug) : null;
      } catch {
        return null;
      }
    },

    async resolveTagForLang(targetLang) {
      const info = this.parseServiceTagPath(this.$route.path);
      if (!info) return null;
      try {
        const src = await fetchPageIdByCode("home", info.lang);
        const dst = await fetchPageIdByCode("home", targetLang);
        const source_page_id = Number(typeof src === "number" ? src : (src?.page_id ?? src?.id));
        const target_page_id = Number(typeof dst === "number" ? dst : (dst?.page_id ?? dst?.id));
        if (!Number.isFinite(source_page_id) || !Number.isFinite(target_page_id)) return info.tag;

        // Optionnel: si tu ajoutes un endpoint côté serveur, ça fera une vraie résolution.
        const { data } = await http.get("services/resolve-tag", {
          params: { source_page_id, target_page_id, tag: info.tag },
        });
        return data?.found && data?.tag ? String(data.tag) : info.tag;
      } catch {
        return info.tag;
      }
    },

    mapRouteNameForLang(curName, targetLang) {
      if (targetLang === "fr" && curName === "about-en") return "about-fr";
      if (targetLang === "en" && curName === "about-fr") return "about-en";
      return curName || undefined;
    },

    async changeLanguage(lang) {
      this.isLangDropdownOpen = false;
      this.setLanguage(lang);

      const { name, params, query, hash, path } = this.$route;
      const safeQuery = { ...query }; // ✅ préserve ?popular=1, ?preview=1, pid, etc.

      // 1) Détail service
      const isDetail = this.parseServiceDetailPath(path);
      if (isDetail && name === "service-detail") {
        const resolved = await this.resolveDetailSlugForLang(lang);
        const targetSlug = resolved || isDetail.slug; // fallback: même slug
        this.$router
          .push({
            name: "service-detail",
            params: { ...params, langue: lang, slug: targetSlug },
            query: safeQuery,
            hash,
          })
          .catch(() => {});
        this.fetchPages(lang);
        return;
      }

      // 2) Liste par tag
      const isTag = this.parseServiceTagPath(path);
      if (isTag && name === "services-by-tag") {
        const targetTag = await this.resolveTagForLang(lang); // fallback = tag identique
        this.$router
          .push({
            name: "services-by-tag",
            params: { ...params, langue: lang, tag: targetTag },
            query: safeQuery,
            hash,
          })
          .catch(() => {});
        this.fetchPages(lang);
        return;
      }

      // 3) Autres routes nommées (about-fr/about-en inclus)
      const targetName = this.mapRouteNameForLang(name, lang);
      if (targetName) {
        this.$router
          .push({
            name: targetName,
            params: { ...params, langue: lang },
            query: safeQuery,
            hash,
          })
          .catch(() => {});
        this.fetchPages(lang);
        return;
      }

      // 4) Dernier recours: remplacer le préfixe dans fullPath en conservant query/hash
      const full = this.$route.fullPath || "/";
      const next = /^\/(fr|en)\b/.test(full) ? full.replace(/^\/(fr|en)/, `/${lang}`) : `/${lang}`;
      if (next !== full) this.$router.push(next).catch(() => {});
      this.fetchPages(lang);
    },

    // ---------------- UI helpers ----------------
    toggleLangDropdown() {
      this.isLangDropdownOpen = !this.isLangDropdownOpen;
    },
    handleClickOutside(e) {
      const wrap = this.$el;
      if (wrap && !wrap.contains(e.target)) this.isLangDropdownOpen = false;
    },
    isActive(targetPath) {
      try {
        const cur = this.$route.path || "/";
        const normalized = targetPath.replace(/\/+$/, "");
        return cur.startsWith(normalized);
      } catch {
        return false;
      }
    },
    onScroll() {
      this.scrolled = window.scrollY > 4;
    },
    hoverUnderlineClass() {
      return "opacity-100";
    },
  },

  mounted() {
    // 👉 Langue depuis l’URL prioritaire
    const langFromRoute = String(this.$route.params.langue || this.langue || "fr");
    if (langFromRoute !== this.langue) this.setLanguage(langFromRoute);

    this.fetchPages(this.langue);

    document.addEventListener("click", this.handleClickOutside);
    this.onScroll();
    window.addEventListener("scroll", this.onScroll, { passive: true });

    // Fermer menus sur navigation
    this._unwatchRoute = this.$watch(
      () => this.$route.fullPath,
      () => {
        this.isMobileMenuOpen = false;
        this.isLangDropdownOpen = false;
      }
    );

    // Recharger la nav quand la langue d’URL change
    this.$watch(
      () => this.$route.params.langue,
      (newLang) => {
        const lang = String(newLang || this.langue || "fr");
        if (lang !== this.langue) this.setLanguage(lang);
        this.fetchPages(lang);
      }
    );
  },

  beforeUnmount() {
    document.removeEventListener("click", this.handleClickOutside);
    window.removeEventListener("scroll", this.onScroll);
    if (this._unwatchRoute) this._unwatchRoute();
  }
};
</script> -->

<script>
import { mapGetters } from "vuex";
import http from "@/api/http";
import { fetchPageIdByCode } from "@/utils/pageUtils";

export default {
  name: "HighHeader",
  data() {
    return {
      navLinks: [],
      isMobileMenuOpen: false,
      isLangDropdownOpen: false,
      scrolled: false,
      _unwatchRoute: null,
    };
  },
  computed: {
    ...mapGetters(["getLangue"]),
    langue: {
      get() {
        return this.getLangue;
      },
      set(v) {
        this.$store.commit("setLangue", v);
      },
    },
  },
  methods: {
    /**
     * RÉALISME TECHNIQUE : 
     * L'utilisation de apiUrl() ici créait le doublon.
     * En passant directement "GlobalContent" à l'instance http, 
     * Axios utilise sa baseURL (/api) et ajoute les paramètres proprement.
     */
    async fetchPages(lang) {
      const l = String(lang || this.$route.params.langue || this.langue || "fr").trim();

      if (!l || l === "undefined" || l === "null") return;

      try {
        const { data } = await http.get("GlobalContent", {
          params: {
            type: "navHeader",
            langue: l
          }
        });
        this.navLinks = Array.isArray(data?.boutons) ? data.boutons : [];
      } catch (e) {
        if (e.response && e.response.status === 400) return;
        console.error("[HighHeader] Erreur chargement nav:", e);
      }
    },

    // ---------------- Utils liens ----------------
    isExternal(path) {
      return /^https?:\/\//i.test(String(path || ""));
    },

    ensureLocalePrefix(path, lang) {
      if (!path) return `/${lang}`;
      if (this.isExternal(path)) return path;
      if (/^\/(fr|en)\b/.test(path)) return path.replace(/^\/(fr|en)/, `/${lang}`);
      return path.startsWith("/") ? `/${lang}${path}` : `/${lang}/${path}`;
    },

    localized(path) {
      return this.ensureLocalePrefix(path, this.langue);
    },

    setLanguage(lang) {
      this.$store.commit("setLangue", lang);
      localStorage.setItem("locale", lang);
    },

    // ---------------- Résolution de Slugs (Multi-langue) ----------------
    parseServiceDetailPath(path) {
      const m = /^\/(fr|en)\/services\/([^\/?#]+)/i.exec(String(path || ""));
      return m ? { lang: m[1], slug: decodeURIComponent(m[2]) } : null;
    },

    parseServiceTagPath(path) {
      const m = /^\/(fr|en)\/services\/tag\/([^\/?#]+)/i.exec(String(path || ""));
      return m ? { lang: m[1], tag: decodeURIComponent(m[2]) } : null;
    },

    async resolveDetailSlugForLang(targetLang) {
      const info = this.parseServiceDetailPath(this.$route.path);
      if (!info) return null;
      try {
        const srcId = await fetchPageIdByCode("home", info.lang);
        const dstId = await fetchPageIdByCode("home", targetLang);

        const { data } = await http.get("service-detail/resolve-slug", {
          params: {
            source_page_id: srcId,
            target_page_id: dstId,
            slug: info.slug
          },
        });
        return data?.found && data?.slug ? String(data.slug) : null;
      } catch (e) {
        console.error("[HighHeader] Erreur resolve-slug:", e);
        return null;
      }
    },

    async resolveTagForLang(targetLang) {
      const info = this.parseServiceTagPath(this.$route.path);
      if (!info) return null;
      try {
        const srcId = await fetchPageIdByCode("home", info.lang);
        const dstId = await fetchPageIdByCode("home", targetLang);

        const { data } = await http.get("services/resolve-tag", {
          params: {
            source_page_id: srcId,
            target_page_id: dstId,
            tag: info.tag
          },
        });
        return data?.found && data?.tag ? String(data.tag) : info.tag;
      } catch {
        return info.tag;
      }
    },

    mapRouteNameForLang(curName, targetLang) {
      if (targetLang === "fr" && curName === "about-en") return "about-fr";
      if (targetLang === "en" && curName === "about-fr") return "about-en";
      return curName || undefined;
    },

    async changeLanguage(lang) {
      this.isLangDropdownOpen = false;
      this.setLanguage(lang);

      const { name, params, query, hash, path } = this.$route;
      const safeQuery = { ...query };

      // 1) Cas Service Detail
      const isDetail = this.parseServiceDetailPath(path);
      if (isDetail && name === "service-detail") {
        const resolved = await this.resolveDetailSlugForLang(lang);
        this.$router.push({
          name: "service-detail",
          params: { ...params, langue: lang, slug: resolved || isDetail.slug },
          query: safeQuery,
          hash,
        }).catch(() => { });
        this.fetchPages(lang);
        return;
      }

      // 2) Cas Liste par Tag
      const isTag = this.parseServiceTagPath(path);
      if (isTag && name === "services-by-tag") {
        const targetTag = await this.resolveTagForLang(lang);
        this.$router.push({
          name: "services-by-tag",
          params: { ...params, langue: lang, tag: targetTag },
          query: safeQuery,
          hash,
        }).catch(() => { });
        this.fetchPages(lang);
        return;
      }

      // 3) Autres routes
      const targetName = this.mapRouteNameForLang(name, lang);
      if (targetName) {
        this.$router.push({
          name: targetName,
          params: { ...params, langue: lang },
          query: safeQuery,
          hash,
        }).catch(() => { });
      } else {
        const full = this.$route.fullPath || "/";
        const next = /^\/(fr|en)\b/.test(full) ? full.replace(/^\/(fr|en)/, `/${lang}`) : `/${lang}`;
        if (next !== full) this.$router.push(next).catch(() => { });
      }
      this.fetchPages(lang);
    },

    // ---------------- UI Helpers ----------------
    toggleLangDropdown() {
      this.isLangDropdownOpen = !this.isLangDropdownOpen;
    },
    handleClickOutside(e) {
      if (this.$el && !this.$el.contains(e.target)) {
        this.isLangDropdownOpen = false;
      }
    },
    isActive(targetPath) {
      const cur = this.$route.path || "/";
      return cur.startsWith(targetPath.replace(/\/+$/, ""));
    },
    onScroll() {
      this.scrolled = window.scrollY > 4;
    },
  },

  mounted() {
    const langFromRoute = this.$route.params.langue || this.langue || "fr";
    if (langFromRoute !== this.langue) this.setLanguage(langFromRoute);

    this.fetchPages(this.langue);

    document.addEventListener("click", this.handleClickOutside);
    window.addEventListener("scroll", this.onScroll, { passive: true });

    this._unwatchRoute = this.$watch(
      () => this.$route.fullPath,
      () => {
        this.isMobileMenuOpen = false;
        this.isLangDropdownOpen = false;
      }
    );

    this.$watch(
      () => this.$route.params.langue,
      (newLang) => {
        if (newLang && newLang !== this.langue) {
          this.setLanguage(newLang);
          this.fetchPages(newLang);
        }
      }
    );
  },

  beforeUnmount() {
    document.removeEventListener("click", this.handleClickOutside);
    window.removeEventListener("scroll", this.onScroll);
    if (this._unwatchRoute) this._unwatchRoute();
  }
};
</script>

<style scoped>
/* Transition du menu mobile */
.slide-enter-active,
.slide-leave-active {
  transition: transform 0.18s ease, opacity 0.18s ease;
}

.slide-enter-from,
.slide-leave-to {
  transform: translateY(-6px);
  opacity: 0;
}
</style>
